import { captureException, startSpan } from "@sentry/react";

import { IUpdateUserRequest, IUserResponse, IUserStats } from "../../types";
import {
    doDelete,
    doGet,
    doGetOptAuth,
    doPost,
    doPostJson,
} from "../core/http";

export const registerUser = async (): Promise<boolean> => {
    try {
        const { data, error } = await startSpan(
            { name: "registerUser" },
            () => {
                return doPost("/user/register", true);
            }
        );

        if (error) {
            captureException(error);
            return false;
        }

        return data as boolean;
    } catch (e) {
        captureException(e);
        return false;
    }
};

export const getCurrentUserInfo = async (): Promise<IUserResponse> => {
    try {
        const { data, error } = await startSpan({ name: "getUserInfo" }, () => {
            return doGet("/user/user", true);
        });

        if (error) {
            captureException(error);
            return { error: "Failed to get user" };
        }

        return {
            user: data,
        };
    } catch (e) {
        captureException(e);
        return { error: "Failed to get user" };
    }
};

export const getCurrentUserFullInfo = async (): Promise<IUserResponse> => {
    try {
        const { data, error } = await startSpan(
            { name: "getUserFullInfo" },
            () => {
                return doGet("/user/user/details", true);
            }
        );

        if (error) {
            captureException(error);
            return { error: "Failed to get user details" };
        }

        return {
            user: data,
        };
    } catch (e) {
        captureException(e);
        return { error: "Failed to get user details" };
    }
};

export const getUserInfo = async (username: string): Promise<IUserResponse> => {
    try {
        const { data, error } = await startSpan(
            { name: "getUserInfoByUsername" },
            () => {
                return doGet(`/user/username/${username}`, false);
            }
        );

        if (error) {
            if (error.status !== 404) {
                captureException(error);
            }
            return { error: "Failed to get user" };
        }

        return {
            user: data,
        };
    } catch (e) {
        captureException(e);
        return { error: "Failed to get user" };
    }
};

export const getUserStats = async (
    userId: string
): Promise<IUserStats | { error: string }> => {
    try {
        const { data, error } = await startSpan(
            { name: "getUserStats" },
            () => {
                return doGet(`/user/${userId}/stats`, false);
            }
        );

        if (error) {
            if (error.status !== 404) {
                captureException(error);
            }
            return { error: "Failed to get user stats" };
        }

        return data as IUserStats;
    } catch (e) {
        captureException(e);
        return { error: "Failed to get user stats" };
    }
};

export const updateUserProfile = async (profileData: {
    bio?: string;
    avatarId?: string;
}): Promise<IUserResponse> => {
    try {
        const formData = new FormData();
        if (profileData.bio) formData.append("bio", profileData.bio);
        if (profileData.avatarId)
            formData.append("avatarId", profileData.avatarId);

        const { data, error } = await startSpan(
            { name: "updateUserProfile" },
            () => {
                return doPost("/user/updateProfile", true, formData);
            }
        );

        if (error) {
            captureException(error);
            return { error: "Failed to update profile" };
        }

        return { user: data };
    } catch (e) {
        captureException(e);
        return { error: "Failed to update profile" };
    }
};

export const followUser = async (
    userId: string
): Promise<{ success: boolean; error?: string }> => {
    try {
        const response = await startSpan({ name: "followUser" }, () => {
            return doPost(`/user/${userId}/follow`, true);
        });

        if (response.error) {
            captureException(response.error); // Capture error before returning
            return { success: false, error: response.error };
        }

        return { success: true };
    } catch (e) {
        captureException(e); // Capture unexpected errors
        console.error("Error following user:", e);
        return { success: false, error: "Failed to follow user" };
    }
};

export const unfollowUser = async (
    userId: string
): Promise<{ success: boolean; error?: string }> => {
    try {
        const response = await startSpan({ name: "unfollowUser" }, () => {
            return doDelete(`/user/${userId}/unfollow`, true);
        });

        if (response.error) {
            captureException(response.error); // Capture error
            return { success: false, error: response.error };
        }

        return { success: true };
    } catch (e) {
        captureException(e); // Capture unexpected errors
        console.error("Error unfollowing user:", e);
        return { success: false, error: "Failed to unfollow user" };
    }
};

export const checkUsernameAvailability = async (
    username: string
): Promise<boolean> => {
    try {
        const url = `/user/username/${username}/validate`;
        const { data, error } = await startSpan(
            { name: "checkUsernameAvailability" },
            () => {
                return doGetOptAuth(url);
            }
        );

        if (error) {
            // Don't capture, expected for unavailable usernames
            // captureException(error);
            return false;
        }

        return data;
    } catch (e) {
        captureException(e);
        return false;
    }
};

export const updateUser = async (
    userUpdate: IUpdateUserRequest
): Promise<{ error?: string }> => {
    const url = "/user";
    // Payload construction remains the same, just ensure IUpdateUserRequest is imported
    const payload: IUpdateUserRequest = {
        username: userUpdate.username,
        marketingConsent: userUpdate.marketingConsent,
        defaultPageSize: userUpdate.defaultPageSize,
    };
    try {
        const { error } = await startSpan({ name: "updateUser" }, () => {
            return doPostJson(url, true, payload);
        });

        if (error) {
            captureException(error);
            return { error: "Failed to update user." };
        }

        return {};
    } catch (e) {
        captureException(e);
        if (e === "User is not logged in.") {
            return { error: e };
        }
        return { error: "Failed to update user." };
    }
};
